import React from 'react';
import AutosizeInput from 'react-input-autosize';

import { bemBlock } from '@neptune/shared/venus-ui';

import './InlineInput.less';

type InlineInputProps = Omit<React.ComponentProps<typeof AutosizeInput>, 'onSubmit' | 'value'> & {
  value: string;
  onSubmit?(value: string): void;
  onCancel?(): void;
  noAutoSize?: boolean;
};

const block = bemBlock('n-inline-input');

export function InlineInput({
  className,
  onSubmit,
  inputRef,
  value,
  ref,
  onCancel,
  noAutoSize,
  ...props
}: InlineInputProps) {
  const skipChangeEvent = React.useRef(false);
  const [newValue, setNewValue] = React.useState(value);
  const [isFocused, setIsFocused] = React.useState(false);

  const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNewValue(event.target.value);
  }, []);

  const onBlur = React.useCallback(() => {
    if (skipChangeEvent.current) {
      setNewValue(value);
      onCancel?.();
    } else {
      onSubmit?.(newValue);
    }
  }, [newValue, onCancel, onSubmit, value]);

  const onFocus = React.useCallback(() => {
    skipChangeEvent.current = false;
    setIsFocused(true);
  }, []);

  const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.currentTarget;

    if (event.key === 'Escape') {
      skipChangeEvent.current = true;
      target.blur();
    }

    if (event.key === 'Enter') {
      skipChangeEvent.current = false;
      target.blur();
    }
  }, []);

  const displayValue = isFocused ? newValue : value;

  if (!noAutoSize) {
    return (
      <AutosizeInput
        {...props}
        inputClassName={block({ element: 'input', extra: className })}
        className={block('wrapper')}
        style={{ display: 'flex' }}
        onKeyDown={onKeyDown}
        value={displayValue}
        title={displayValue}
        inputRef={inputRef}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        type="text"
      />
    );
  }

  return (
    <input
      {...props}
      className={block({ element: 'input', extra: className })}
      onKeyDown={onKeyDown}
      value={displayValue}
      title={displayValue}
      ref={inputRef}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      type="text"
    />
  );
}
